import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { FieldType } from "@shoothill/core";
import { AppUrls } from "AppUrls";
import isLandLine from "Globals/validators/LandLine";

// Models
import { PersonalDetailsModel } from "Globals/Models/Domain/FormSections/PersonalDetails/PersonalDetailsModel";
import { PersonalDetailsDTO } from "Globals/Models/DTO/PersonalDetailsDTO";
import { FormSectionsEnum } from "Globals/Models/FormSectionsEnum";
import FormSectionViewModelBase from "Globals/ViewModels/FormSections/FormSectionViewModelBase";
import FamilyAddressViewModel from "Globals/ViewModels/FormSections/PersonalDetails/FamilyAddressViewModel";
// View models
import PereviousAddressViewModel from "Globals/ViewModels/FormSections/PersonalDetails/PreviousAddressViewModel";
import { action, computed, observable } from "mobx";
import moment from "moment";
import validator from "validator";

export default class PersonalDetailsViewModel extends FormSectionViewModelBase<PersonalDetailsModel> {
    @observable public previousAddressViewModels: PereviousAddressViewModel[] = [];
    @observable public familyAddressViewModels: FamilyAddressViewModel[] = [];

    constructor(personalDetailsModel: PersonalDetailsModel, _updateSectionCompleted: (index: FormSectionsEnum, value: boolean) => void) {
        super(_updateSectionCompleted, personalDetailsModel);
        this.saveStore.addCallback(FormSectionsEnum.PersonalDetails, this.save);
        this.saveFunction = this.save; // This is the save function that will be used by the form section base to auto save
        this.createViewModels();
        if (this.updateSectionCompleted) {
            this.updateSectionCompleted(FormSectionsEnum.PersonalDetails, this.canProceed);
        }
    }

    @action
    private createViewModels(): void {
        if (this.model.previousAddressModels) {
            for (const previousAddress of this.model.previousAddressModels) {
                this.previousAddressViewModels.push(new PereviousAddressViewModel(previousAddress));
            }
        }
        if (this.model.familyAddressModels) {
            for (const previousAddress of this.model.familyAddressModels) {
                this.familyAddressViewModels.push(new FamilyAddressViewModel(previousAddress));
            }
        }
    }

    @computed
    public get getSortedPreviousAddressViewModels(): PereviousAddressViewModel[] {
        return this.previousAddressViewModels
            .slice()
            .sort((a, b) => (b.model.fromDate ? new Date(b.model.fromDate).getTime() : 0) - (a.model.fromDate ? new Date(a.model.fromDate).getTime() : 0));
    }
    @computed
    public get getSortedFamilyAddressViewModels(): FamilyAddressViewModel[] {
        return this.familyAddressViewModels
            .slice()
            .sort((a, b) => (b.model.createdDate ? new Date(b.model.createdDate).getTime() : 0) - (a.model.createdDate ? new Date(a.model.createdDate).getTime() : 0));
    }

    @action
    public handleInputChange = (): void => {
        this.updateSectionCompleted(FormSectionsEnum.PersonalDetails, this.canProceed);
        this.saveStore.setIsSaved(false);
        this.restartTimer();
        this.saveFunction = this.save;
    };

    public save = async (): Promise<void> => {
        try {
            let isMyModelValid: boolean = await this.isModelValid();
            let isDirty = this.isModelDirty;
            if (isMyModelValid && isDirty == true) {
                this.setIsLoading(true);
                this.saveStore.setIsSaving(true);

                let upsertPersonalDetails: PersonalDetailsDTO = this.model.toDto();
                const apiResult = await this.Post<PersonalDetailsDTO>(AppUrls.Server.ApplicationForm.Upsert.PersonalDetails, upsertPersonalDetails);

                if (apiResult.wasSuccessful) {
                    this.model.fromPersonalDetailsDto(apiResult.payload);
                    console.info("PersonalDetails:Upsert-Successful");
                    // Will set this section as completed when canProceed returns true
                    this.updateSectionCompleted(FormSectionsEnum.PersonalDetails, this.canProceed);

                    let childForms = this.familyAddressViewModels.filter((a) => !a.model.personalDetailId);
                    childForms.map(async (c) => {
                        await c.saveFromStash(apiResult.payload.id as string);
                    });

                    let previousForms = this.previousAddressViewModels.filter((a) => !a.model.personalDetailId);
                    previousForms.map(async (c) => {
                        await c.saveFromStash(apiResult.payload.id as string);
                    });

                    this.setModelDirty(false);
                    return Promise.resolve();
                } else {
                    console.error("PersonalDetails:Upsert-Error", apiResult.errors);
                    return Promise.reject(apiResult.errors);
                }
            }
        } catch (exception) {
            console.log("Error:", exception);
        } finally {
            this.setIsLoading(false);
            this.saveStore.setIsSaving(false);
            this.saveStore.setIsSaved(true);
        }
    };

    @action
    public handleRadioChange = (fieldName: keyof FieldType<PersonalDetailsModel>, val: string): void => {
        this.model.setValue(fieldName, val == "1" ? true : false);
        this.model.setDirty(fieldName, true);
        this.handleInputChange();
    };

    @action
    public setDateOfBirth(val: MaterialUiPickersDate) {
        if (val !== null) {
            this.setValue("dateOfBirth", moment(val).utc().toISOString());
        } else {
            this.setValue("dateOfBirth", val);
        }
        this.model.setDirty("dateOfBirth", true);
        this.handleInputChange();
    }

    @computed
    public get canProceed(): boolean {
        let completed = true;
        Object.keys(this.model).forEach((_field) => {
            let field = _field as keyof FieldType<PersonalDetailsModel>;
            let value = this.model.getValue(field);
            if (field == "homeTelephone") return;
            if (field == "previousAddressModels") return;
            if (field == "familyAddressModels") return;
            if (field == "addressLine2") return;
            if (field == "addressLine3") return;
            if (field == "middleName") return;
            if (field == "workLocalAuthorityId") {
                if (this.model.employed == true) {
                    if (this.isEmptyNullOrUndefined(value)) {
                        completed = false;
                    }
                }
                return;
            }
            if (this.isEmptyNullOrUndefined(value as any)) {
                completed = false;
            }
            if (completed == false) {
                console.log("Field: ", field, "Value: ", value);
            }
        });
        if (!(this.model.yearsAtAddress >= 3)) {
            if (this.model.livedElsewhereInLastThreeYears == true) {
                if (this.previousAddressViewModels.length < 1) {
                    completed = false;
                }
            }
        }

        if (this.model.immediateFamilyInLocalAuthority == true) {
            if (this.familyAddressViewModels.length < 1) {
                completed = false;
            }
        }
        if (this.model.employed == true) {
            if (this.isEmptyNullOrUndefined(this.model.workLocalAuthorityId)) {
                completed = false;
            }
        }
        return completed;
    }

    public async isFieldValid(fieldName: keyof FieldType<PersonalDetailsModel>, value: any): Promise<boolean> {
        return await this.isValid(fieldName, value, false);
    }

    public async isValid(fieldName: keyof FieldType<PersonalDetailsModel>, value: any, isRequired: boolean = false): Promise<boolean> {
        let { isValid, errorMessage } = await this.validateDecorators(fieldName);
        if (fieldName == "relationshipTypeId") {
            if ((value && !validator.isEmpty(value) && value != "placeholder") || isRequired) {
                if (validator.isEmpty(value ? value : "")) {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
            }
        }
        if (fieldName == "title") {
            if ((value && !validator.isEmpty(value) && value != "placeholder") || isRequired) {
                if (validator.isEmpty(value ? value : "")) {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
            }
        }
        if (fieldName == "firstName") {
            if ((value && !validator.isEmpty(value)) || isRequired) {
                if (validator.isEmpty(value ? value : "")) {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
            }
        }
        if (fieldName == "lastName") {
            if ((value && !validator.isEmpty(value)) || isRequired) {
                if (validator.isEmpty(value ? value : "")) {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
            }
        }
        if (fieldName == "dateOfBirth") {
            if ((value && !validator.isEmpty(value)) || isRequired) {
                if (value == null || validator.isEmpty((value as Date).toString())) {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
            }
        }
        if (fieldName == "addressLine1") {
            if ((value && !validator.isEmpty(value)) || isRequired) {
                if (validator.isEmpty(value ? value : "")) {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
            }
        }
        if (fieldName == "city") {
            if ((value && !validator.isEmpty(value)) || isRequired) {
                if (validator.isEmpty(value ? value : "")) {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
            }
        }
        if (fieldName == "county") {
            if ((value && !validator.isEmpty(value)) || isRequired) {
                if (validator.isEmpty(value ? value : "")) {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
            }
        }
        if (fieldName == "postcode") {
            if ((value && !validator.isEmpty(value)) || isRequired) {
                if (validator.isEmpty(value ? value : "")) {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
                if (!validator.isPostalCode(value, "GB")) {
                    errorMessage = "not a valid postcode";
                    isValid = false;
                }
            }
        }
        if (fieldName == "livedElsewhereInLastThreeYears") {
            if (!(this.model.yearsAtAddress >= 3)) {
                if (value == true && this.previousAddressViewModels.length < 1) {
                    errorMessage = "provide at least one previous address";
                    isValid = false;
                }
            }
        }
        if (fieldName == "homeTelephone") {
            if (value) {
                if ((value && !validator.isEmpty(value)) || isRequired) {
                    if (value) {
                        if (!isLandLine(value)) {
                            errorMessage = "invalid landline number";
                            isValid = false;
                        }
                    }
                }
            }
        }
        if (fieldName == "mobileTelephone") {
            if ((value && !validator.isEmpty(value)) || isRequired) {
                if (validator.isEmpty(value ? value : "")) {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
                if (!validator.isMobilePhone(value, "en-GB")) {
                    errorMessage = "invalid mobile phone number";
                    isValid = false;
                }
            }
        }
        if (fieldName == "email") {
            if ((value && !validator.isEmpty(value)) || isRequired) {
                if (validator.isEmpty(value ? value : "")) {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
                if (!validator.isEmail(value)) {
                    errorMessage = "email address is not valid";
                    isValid = false;
                }
            }
        }
        if (fieldName == "localAuthorityId") {
            if ((value && !validator.isEmpty(value) && value != "placeholder") || isRequired) {
                if (value == null || validator.isEmpty(value)) {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
            }
        }
        if (fieldName == "immediateFamilyInLocalAuthority") {
            if (value == true && this.familyAddressViewModels.length < 1 && this.model.immediateFamilyInLocalAuthority) {
                errorMessage = "provide at least one family members details";
                isValid = false;
            }
        }
        if (fieldName == "workLocalAuthorityId") {
            if ((value && !validator.isEmpty(value) && value != "placeholder") || isRequired) {
                if (this.model.employed == true && (value == null || validator.isEmpty(value))) {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
            }
        }
        if (isValid == false) {
            console.log("Field Not valid: ", fieldName);
        }
        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);
        return isValid;
    }

    /**
     * @returns boolean does this section have any errors
     */
    @action
    public async showUnfinishedFields(): Promise<boolean> {
        let hasError = false;
        for (const _field of Object.keys(this.model)) {
            let field = _field as keyof FieldType<PersonalDetailsModel>;
            let value = this.model.getValue(field);
            let isRequired = true;
            let isValid = await this.isValid(field, value, isRequired);
            if (isValid == false) {
                hasError = true;
            }
        }
        return hasError;
    }
    public beforeUpdate: undefined;
}
